export const clearAllBrowserData = async () => {
  const clearingResults = {
    localStorage: false,
    sessionStorage: false,
    cookies: false,
    indexedDB: false,
    cacheStorage: false,
    errors: []
  };

  // Clear localStorage
  try {
    localStorage.clear();
    clearingResults.localStorage = true;
  } catch (error) {
    clearingResults.errors.push(`localStorage: ${error.message}`);
  }

  // Clear sessionStorage
  try {
    sessionStorage.clear();
    clearingResults.sessionStorage = true;
  } catch (error) {
    clearingResults.errors.push(`sessionStorage: ${error.message}`);
  }

  // Clear cookies (only non-httpOnly cookies)
  try {
    const cookies = document.cookie.split(";");
    for (let cookie of cookies) {
      const eqPos = cookie.indexOf("=");
      const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
      if (name) {
        // Clear cookie for current domain
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
        // Also try to clear for parent domain
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`;
        // Try with leading dot for subdomain clearing
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=.${window.location.hostname}`;
      }
    }
    clearingResults.cookies = true;
  } catch (error) {
    clearingResults.errors.push(`cookies: ${error.message}`);
  }

  // Clear IndexedDB
  try {
    if ('indexedDB' in window) {
      // Get all database names if supported
      if ('databases' in indexedDB) {
        const databases = await indexedDB.databases();
        for (const db of databases) {
          if (db.name) {
            const deleteRequest = indexedDB.deleteDatabase(db.name);
            await new Promise((resolve, reject) => {
              deleteRequest.onsuccess = () => resolve();
              deleteRequest.onerror = () => reject(deleteRequest.error);
            });
          }
        }
      } else {
        // Fallback: try to delete common database names
        const commonDbNames = ['ChatDB', 'chatDatabase', 'styloq-chat', 'chat-storage'];
        for (const dbName of commonDbNames) {
          try {
            const deleteRequest = indexedDB.deleteDatabase(dbName);
            await new Promise((resolve) => {
              deleteRequest.onsuccess = () => resolve();
              deleteRequest.onerror = () => resolve(); // Don't fail if DB doesn't exist
            });
          } catch (e) {
            // Ignore errors for non-existent databases
          }
        }
      }
      clearingResults.indexedDB = true;
    }
  } catch (error) {
    clearingResults.errors.push(`indexedDB: ${error.message}`);
  }

  // Clear Cache Storage (Service Worker caches)
  try {
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(
        cacheNames.map(cacheName => caches.delete(cacheName))
      );
      clearingResults.cacheStorage = true;
    }
  } catch (error) {
    clearingResults.errors.push(`cacheStorage: ${error.message}`);
  }

  return clearingResults;
};

export const getStorageInfo = async () => {
  const info = {
    localStorage: {
      available: 'localStorage' in window,
      itemCount: 0,
      estimatedSize: 0
    },
    sessionStorage: {
      available: 'sessionStorage' in window,
      itemCount: 0,
      estimatedSize: 0
    },
    indexedDB: {
      available: 'indexedDB' in window,
      databases: []
    },
    cookies: {
      count: 0,
      list: []
    },
    cacheStorage: {
      available: 'caches' in window,
      caches: []
    }
  };

  // Check localStorage
  try {
    if (info.localStorage.available) {
      info.localStorage.itemCount = localStorage.length;
      info.localStorage.estimatedSize = JSON.stringify(localStorage).length;
    }
  } catch (e) {
    // Silent error handling
  }

  // Check sessionStorage
  try {
    if (info.sessionStorage.available) {
      info.sessionStorage.itemCount = sessionStorage.length;
      info.sessionStorage.estimatedSize = JSON.stringify(sessionStorage).length;
    }
  } catch (e) {
    // Silent error handling
  }

  // Check cookies
  try {
    const cookies = document.cookie.split(';').filter(c => c.trim());
    info.cookies.count = cookies.length;
    info.cookies.list = cookies.map(c => c.split('=')[0].trim());
  } catch (e) {
    // Silent error handling
  }

  // Check IndexedDB
  try {
    if (info.indexedDB.available && 'databases' in indexedDB) {
      info.indexedDB.databases = await indexedDB.databases();
    }
  } catch (e) {
    // Silent error handling
  }

  // Check Cache Storage
  try {
    if (info.cacheStorage.available) {
      info.cacheStorage.caches = await caches.keys();
    }
  } catch (e) {
    // Silent error handling
  }

  return info;
};